import type {
  ActivityLogChange,
  ActivityLogEntry,
  ActivityLogGroup,
} from './ActivityLogView';

/**
 * A vehicle's history as the activity log reads it: GET
 * /vehicle-history/vehicle/:vehicleId gives a snapshot of the vehicle per
 * change, newest first, and the log draws what changed between each one and
 * the one before. The dashboard's call panel and the phone's Call activity
 * sheet both draw it; the diffing lived in the dashboard, so the phone had
 * none.
 *
 * Here rather than in @relay-towing/api because what it produces is the
 * library's ActivityLogEntry, and the words in it ("Vehicle Update",
 * "Impound Type") are presentation. Pure: no hooks, no markup.
 */

/**
 * One row of the endpoint. Written by hand: the route declares no response
 * schema, so the OpenAPI document has no type to generate this from.
 */
export interface VehicleHistoryRow {
  id: string;
  vehicleId: string;
  changeDate: string;
  changeSource: string;
  changeType?: string;
  userID?: string;
  /** Attached by the server: whoever made the change. */
  user?: { name?: string | null } | null;
  imageIDs?: string[];
  odometer?: number | null;
  hasKeys?: boolean;
  isReleaseReady?: boolean;
  isJunkReady?: boolean;
  vin: string;
  licensePlate: string;
  vehicleName?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
  vehicleAttributes?: Record<string, unknown> | null;
  impoundStatus?: string | null;
  dispatchStatus?: string | null;
  callReason?: string | null;
}

/** An entry with the raw timestamp, for a caller that merges in other events. */
export type TimedActivityLogEntry = ActivityLogEntry & { at: string };

const TYPE_CONFIG: Record<string, { label: ActivityLogGroup; action: string }> =
  {
    STATUS_CHANGE: { label: 'driver', action: 'Status Change' },
    DISPATCH_UPDATE: { label: 'dispatch', action: 'Dispatch Update' },
    IMPOUND_UPDATE: { label: 'system', action: 'Impound Update' },
    VEHICLE_UPDATE: { label: 'driver', action: 'Vehicle Update' },
  };

/** Who the event is attributed to: a driver, dispatch, or the system. */
const entryGroup = (h: VehicleHistoryRow): ActivityLogGroup => {
  if (h.changeSource === 'PROVIDER_CHAT') return 'system';
  return TYPE_CONFIG[h.changeType ?? '']?.label ?? 'system';
};

const entryAction = (h: VehicleHistoryRow): string =>
  (TYPE_CONFIG[h.changeType ?? ''] ?? TYPE_CONFIG.IMPOUND_UPDATE).action;

// Who made a change: a member's name (renaming them renames their Driver
// rows too), or Relay Chat.
const entryActor = (h: VehicleHistoryRow): string => {
  const name = h.user?.name;
  if (h.changeSource === 'PROVIDER_CHAT') {
    return name ? `${name} via Relay Chat` : 'Relay Chat';
  }
  return name ?? h.userID ?? 'Unknown';
};

// ── Diff helpers ──────────────────────────────────────────────────────────────

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isDatetimeField = (fieldName: string) =>
  [
    'createdAt',
    'updatedAt',
    'deletedAt',
    'date',
    'time',
    'timestamp',
    'dateTime',
  ].some((f) => fieldName.toLowerCase().includes(f.toLowerCase()));

const formatDatetimeValue = (value: unknown): string => {
  if (!value) return '';
  try {
    const d = new Date(value as string);
    if (!isNaN(d.getTime()))
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
  } catch {
    /* noop */
  }
  return String(value);
};

const formatFieldName = (fieldName: string) =>
  fieldName
    .replace(/^attr\./, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

interface ChangeLine {
  text: string;
  type: 'add' | 'remove' | 'change';
}

/** The row's own fields, read by name. */
const fieldOf = (h: VehicleHistoryRow, field: keyof VehicleHistoryRow) =>
  (h as unknown as Record<string, unknown>)[field];

const isEmpty = (value: unknown) => value === '' || value == null;

const ignoredAttribute = (key: string) =>
  ['vinDecodedAt', 'vindecodedat'].includes(key.toLowerCase());

const computeDiff = (
  current: VehicleHistoryRow,
  previous?: VehicleHistoryRow
): ChangeLine[] => {
  if (!previous) {
    const changes: ChangeLine[] = [];
    if (current.impoundStatus)
      changes.push({
        text: `Impound Status: ${formatStatus(current.impoundStatus)}`,
        type: 'add',
      });
    if (current.dispatchStatus)
      changes.push({
        text: `Dispatch Status: ${formatStatus(current.dispatchStatus)}`,
        type: 'add',
      });
    if (current.callReason)
      changes.push({ text: `Call Reason: ${current.callReason}`, type: 'add' });
    (
      [
        'vehicleName',
        'vehicleModel',
        'vehicleYear',
        'odometer',
        'vin',
        'licensePlate',
      ] as Array<keyof VehicleHistoryRow>
    ).forEach((f) => {
      const v = fieldOf(current, f);
      if (!isEmpty(v))
        changes.push({ text: `${String(f)}: ${v}`, type: 'add' });
    });
    const attrs = current.vehicleAttributes || {};
    Object.keys(attrs)
      .filter((k) => !ignoredAttribute(k))
      .forEach((k) => {
        const v = attrs[k];
        if (!isEmpty(v)) changes.push({ text: `attr.${k}: ${v}`, type: 'add' });
      });
    const imgCount = (current.imageIDs || []).length;
    if (imgCount > 0)
      changes.push({
        text: `${imgCount} Image${imgCount === 1 ? '' : 's'}`,
        type: 'add',
      });
    return changes;
  }

  const changes: ChangeLine[] = [];
  const changeType = current.changeType;

  const pushStatusDiff = (
    curr: string | null | undefined,
    prev: string | null | undefined,
    label: string
  ) => {
    if (curr === prev) return;
    const a = prev || 'None',
      b = curr || 'None';
    if (a !== b)
      changes.push({
        text: `${label}: ${a === 'None' ? 'None' : formatStatus(a)} → ${b === 'None' ? 'None' : formatStatus(b)}`,
        type: 'change',
      });
  };

  if (changeType === 'IMPOUND_UPDATE') {
    pushStatusDiff(
      current.impoundStatus,
      previous.impoundStatus,
      'Impound Status'
    );
    return changes;
  }

  if (changeType !== 'VEHICLE_UPDATE') {
    pushStatusDiff(
      current.impoundStatus,
      previous.impoundStatus,
      'Impound Status'
    );
    pushStatusDiff(
      current.dispatchStatus,
      previous.dispatchStatus,
      'Dispatch Status'
    );
    if (current.callReason !== previous.callReason) {
      const a = previous.callReason || 'None',
        b = current.callReason || 'None';
      if (a !== b)
        changes.push({ text: `Call Reason: ${a} → ${b}`, type: 'change' });
    }
    if (changes.length > 0) return changes;
  }

  const fields: Array<keyof VehicleHistoryRow> = [
    'vehicleName',
    'vehicleModel',
    'vehicleYear',
    'odometer',
    'hasKeys',
    'isReleaseReady',
    'isJunkReady',
    'vin',
    'licensePlate',
  ];
  fields.forEach((f) => {
    const a = fieldOf(current, f),
      b = fieldOf(previous, f);
    if (a === b) return;
    const fieldName = String(f);
    const prevEmpty = isEmpty(b),
      currEmpty = isEmpty(a);
    if (prevEmpty && !currEmpty)
      changes.push({ text: `${fieldName}: ${a}`, type: 'add' });
    else if (!prevEmpty && currEmpty)
      changes.push({ text: `${fieldName}: ${b}`, type: 'remove' });
    else if (!prevEmpty && !currEmpty)
      changes.push({ text: `${fieldName}: ${b} → ${a}`, type: 'change' });
  });

  const aImgs = (current.imageIDs || []).length,
    bImgs = (previous.imageIDs || []).length;
  if (aImgs !== bImgs) {
    const delta = aImgs - bImgs,
      abs = Math.abs(delta);
    changes.push({
      text: `${abs} Image${abs === 1 ? '' : 's'}`,
      type: delta > 0 ? 'add' : 'remove',
    });
  }

  const currAttrs = current.vehicleAttributes || {},
    prevAttrs = previous.vehicleAttributes || {};
  Object.keys({ ...currAttrs, ...prevAttrs })
    .filter((k) => !ignoredAttribute(k))
    .forEach((k) => {
      const a = currAttrs[k],
        b = prevAttrs[k];
      if (a === b) return;
      const prevEmpty = isEmpty(b),
        currEmpty = isEmpty(a);
      if (prevEmpty && !currEmpty)
        changes.push({ text: `attr.${k}: ${a}`, type: 'add' });
      else if (!prevEmpty && currEmpty)
        changes.push({ text: `attr.${k}: ${b}`, type: 'remove' });
      else if (!prevEmpty && !currEmpty)
        changes.push({ text: `attr.${k}: ${b} → ${a}`, type: 'change' });
    });

  return changes;
};

const buildChangeText = (change: ChangeLine): string => {
  const clean = change.text.replace(/^attr\./, '');
  const colon = clean.indexOf(':');
  if (colon === -1) return clean;
  const rawField = clean.substring(0, colon).trim();
  const value = clean.substring(colon + 1).trim();
  const fieldName = formatFieldName(rawField);
  if (isDatetimeField(rawField)) {
    if (change.type === 'change') {
      const parts = value.split(' → ');
      if (parts.length === 2)
        return `${fieldName}: ${formatDatetimeValue(parts[0])} → ${formatDatetimeValue(parts[1])}`;
    }
    return `${fieldName}: ${formatDatetimeValue(value)}`;
  }
  return `${fieldName}: ${value}`;
};

/** Title-cases values that are entirely uppercase (e.g. "ACURA" → "Acura"). */
const smartCase = (value: string): string => {
  if (/[A-Z]/.test(value) && value === value.toUpperCase()) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return value;
};

/** The field, old and new value the activity log draws for one change. */
const toActivityLogChange = (change: ChangeLine): ActivityLogChange => {
  const text = buildChangeText(change);
  const colon = text.indexOf(': ');

  // No colon (e.g. "1 Image added") — one plain phrase
  if (colon === -1) return { kind: change.type, value: text };

  const field = text.substring(0, colon);
  const value = text.substring(colon + 2);

  if (change.type === 'change') {
    const arrowIdx = value.indexOf(' → ');
    if (arrowIdx !== -1) {
      return {
        kind: 'change',
        field,
        from: smartCase(value.substring(0, arrowIdx)),
        value: smartCase(value.substring(arrowIdx + 3)),
      };
    }
  }

  return {
    kind: change.type === 'add' ? 'add' : 'remove',
    field,
    value: smartCase(value),
  };
};

/**
 * The log's entries for a vehicle's history, newest first as the rows come.
 * A row that changed nothing from the one before is dropped; the oldest row
 * fetched is drawn as everything it added.
 *
 * `when` formats a row's timestamp, because the desk writes "Oct 14, 2026"
 * and the phone's Figma "October 14, 2026".
 *
 *   const entries = vehicleHistoryActivity(rows, formatTimestampParts);
 */
export function vehicleHistoryActivity(
  rows: VehicleHistoryRow[],
  when: (at: string) => { date: string; time: string }
): TimedActivityLogEntry[] {
  return rows
    .map((h, idx) => ({ row: h, changes: computeDiff(h, rows[idx + 1]) }))
    .filter(({ changes }) => changes.length > 0)
    .map(({ row: h, changes }) => {
      const { date, time } = when(h.changeDate);
      return {
        id: h.id,
        action: entryAction(h),
        actor: entryActor(h),
        date,
        time,
        group: entryGroup(h),
        changes: changes.map(toActivityLogChange),
        at: h.changeDate,
      };
    });
}
